export type StringDevtoolsOverlayId = string;

export const STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT = "string-devtools-overlay-layout:change";

const SLOT_STEP = 31;
const COLLISION_STEP = 33;
const COLLISION_SIZE_X = 50;
const COLLISION_SIZE_Y = 31;
const BASE_OFFSET_X = 0;
const BASE_OFFSET_Y = 0;

interface OverlayEntry {
  order: number;
  badgeCount: number;
}

export interface AnchorPoint {
  docX: number;
  docY: number;
}

export class OverlaySlotRegistry {
  private readonly entries = new Map<string, OverlayEntry>();

  register(id: string, order: number, badgeCount = 1): void {
    this.entries.set(id, { order, badgeCount });
  }

  unregister(id: string): void {
    this.entries.delete(id);
  }

  resolveAnchor(
    overlayId: StringDevtoolsOverlayId,
    contentX: number,
    contentY: number,
  ): AnchorPoint {
    const sorted = this.getSorted();
    const index = sorted.findIndex((o) => o.id === overlayId);

    let slotOffset = 0;
    for (let i = 0; i < index; i += 1) {
      slotOffset += sorted[i].badgeCount;
    }

    return {
      docX: contentX + BASE_OFFSET_X + slotOffset * SLOT_STEP,
      docY: contentY + BASE_OFFSET_Y,
    };
  }

  resolveCollisionOffset(
    element: HTMLElement,
    baseDocX: number,
    baseDocY: number,
    resolveAncestorAnchor: (ancestorId: string) => AnchorPoint | null,
  ): number {
    const ancestorAnchors: AnchorPoint[] = [];
    let current = element.parentElement;

    while (current) {
      const ancestorId =
        current.getAttribute("string-id") ?? current.getAttribute("data-string-id");
      if (ancestorId) {
        const anchor = resolveAncestorAnchor(ancestorId);
        if (anchor) {
          ancestorAnchors.push(anchor);
        }
      }
      current = current.parentElement;
    }

    let offset = 0;
    let hasCollision = true;

    while (hasCollision) {
      hasCollision = false;

      for (const anchor of ancestorAnchors) {
        if (
          Math.abs(anchor.docX - baseDocX) < COLLISION_SIZE_X &&
          Math.abs(anchor.docY - (baseDocY + offset)) < COLLISION_SIZE_Y
        ) {
          offset += COLLISION_STEP;
          hasCollision = true;
          break;
        }
      }
    }

    return offset;
  }

  emitLayoutChange(): void {
    window.dispatchEvent(new CustomEvent(STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT));
  }

  private getSorted(): Array<{ id: string } & OverlayEntry> {
    const list: Array<{ id: string } & OverlayEntry> = [];
    for (const [id, entry] of this.entries) {
      list.push({ id, ...entry });
    }
    list.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.id.localeCompare(b.id);
    });
    return list;
  }
}

export const overlaySlotRegistry = new OverlaySlotRegistry();
